// LanguageButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import getCurrentLanguage, {
  getLanguageTranslationKey,
} from '../resource/getCurrentLanguage.js';
import trackMixpanel, {
  EventTypes,
  getViewId,
  ButtonId,
} from '../resource/mixpanel.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { color } from '../style/variables.js';

import LanguageTealblueIconSource from '../../img/ic_language_tealblue.svg';
import LanguageWhiteIconSource from '../../img/ic_language_white.svg';
import NextTealblueIconSource from '../../img/ic_next_tealblue.svg';
import NextWhiteIconSource from '../../img/ic_next_white.svg';

export const LayoutType = {
  HAMBURGER: 'hamburger',
};

export const LanguageButton = ({
  modalViewRootId,
  layoutType,
  isLanguageMenuOpened,
  t,
  i18n,
  toggleModal,
}) => {
  const isHamburger = LayoutType.HAMBURGER === layoutType;
  return (
    <StyledLanguageButton
      isHamburger={isHamburger}
      id={modalViewRootId}
      onClick={event => {
        event.stopPropagation();
        trackMixpanel({
          type: EventTypes.USER_CLICKED,
          payload: {
            viewId: getViewId({
              pathname: window.location.pathname,
            }),
            buttonId: ButtonId.Setting.ButtonLanguage,
          },
        });
        if (isHamburger) {
          toggleModal({
            modalId: 'HamburgerMenuModal',
            isOpen: false,
          });
        }
        return toggleModal({
          modalId: 'LanguageMenu',
          isOpen: !isLanguageMenuOpened,
          payload: {
            modalViewRootId,
          },
        });
      }}
      isMenuOpened={isLanguageMenuOpened}
    >
      <Title isHamburger={isHamburger} isMenuOpened={isLanguageMenuOpened}>
        <LanguageIcon
          isHamburger={isHamburger}
          src={
            isHamburger
              ? LanguageWhiteIconSource
              : isLanguageMenuOpened
              ? LanguageTealblueIconSource
              : LanguageWhiteIconSource
          }
        />
        {isHamburger ? t('language') : null}
      </Title>
      <SelectedLanguage
        isHamburger={isHamburger}
        isMenuOpened={isLanguageMenuOpened}
      >
        {t(
          getLanguageTranslationKey({
            language: i18n.language || getCurrentLanguage(),
          })
        )}
        <NextIcon
          isHamburger={isHamburger}
          width={isHamburger ? 24 : 16}
          height={isHamburger ? 24 : 16}
          src={
            isHamburger
              ? NextWhiteIconSource
              : isLanguageMenuOpened
              ? NextTealblueIconSource
              : NextWhiteIconSource
          }
        />
      </SelectedLanguage>
    </StyledLanguageButton>
  );
};

LanguageButton.propTypes = {
  modalViewRootId: PropTypes.string,
  layoutType: PropTypes.string,
  isLanguageMenuOpened: PropTypes.bool,
  i18n: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  toggleModal: PropTypes.func,
};

LanguageButton.defaultProps = {
  modalViewRootId: null,
  layoutType: null,
  isLanguageMenuOpened: false,
  toggleModal: () => null,
};

const StyledLanguageButton = styled.button.attrs(
  ({ isMenuOpened, isHamburger }) => ({
    style: {
      borderColor: isMenuOpened ? color.tealBlue : color.neutral[100],
      borderWidth: isHamburger ? 'medium' : '1px',
      borderStyle: isHamburger ? 'none' : 'solid',
      color: isMenuOpened ? color.tealBlue : color.neutral[100],
      width: isHamburger ? '100%' : 'width',
    },
  })
)`
  ${ResetButtonStyle};
  position: relative;
  border-radius: 4px;
  padding: 0px 8px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div.attrs(({ isHamburger, isMenuOpened }) => ({
  style: {
    color: isHamburger
      ? color.neutral[100]
      : isMenuOpened
      ? color.tealBlue
      : color.neutral[100],
  },
}))`
  display: flex;
  align-items: center;
  font-size: 14px;
`;

const LanguageIcon = styled.img.attrs(({ isHamburger }) => ({
  style: {
    marginRight: `${isHamburger ? 8 : 4}px`,
  },
}))`
  width: 16px;
  height: 16px;
  object-fit: contain;
`;

const SelectedLanguage = styled.div.attrs(({ isHamburger, isMenuOpened }) => ({
  style: {
    color: isHamburger
      ? color.neutral[30]
      : isMenuOpened
      ? color.tealBlue
      : color.neutral[100],
  },
}))`
  display: flex;
  align-items: center;
  font-size: 14px;
`;

const NextIcon = styled.img.attrs(({ isHamburger }) => ({
  style: {
    width: `${isHamburger ? 24 : 16}px`,
    height: `${isHamburger ? 24 : 16}px`,
  },
}))`
  margin-left: 4px;
  object-fit: contain;
`;

export default withTranslation()(LanguageButton);
